<template>
    <div class="speech-overlay">
        <div
            id="soap-speech-to-text"
            ref="modal"
            :class="{ options: isShowOptions }"
            :style="{ top: `${position.top}px`, left: `${position.left}px` }"
        >
            <div class="options-overlay" v-if="isShowOptions">
                <div class="options-content">
                    <h1>OPTIONS [DEV ONLY]</h1>
                    <div class="item">
                        <div class="label">Transcription</div>
                        <span class="description">
                            Select the provider to process audio to get the
                            transcription.
                        </span>
                        <div>
                            <select v-model="options.speech">
                                <option value="amazon" disabled>
                                    Amazon Transcribe
                                </option>
                                <option value="openai">
                                    OpenAI Whisper
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="item">
                        <div class="label">LLM Model</div>
                        <span class="description">
                            Select the LLM model to generate or process the
                            transcription.
                        </span>
                        <div>
                            <select v-model="options.llm">
                                <option value="CLAUDE_3_OPUS" disabled>
                                    Claude 3 Opus
                                </option>
                                <option value="CLAUDE_35_SONNET" disabled>
                                    Claude 3.5 Sonnet
                                </option>
                                <option value="CLAUDE_35_HAIKU" disabled>
                                    Claude 3.5 Haiku
                                </option>
                                <option value="CLAUDE_3_HAIKU" disabled>
                                    Claude 3 Haiku
                                </option>
                                <option value="gpt-4o">GPT-4o</option>
                                <option value="gpt-4o-mini">
                                    GPT-4o-mini
                                </option>
                                <option value="gpt-4">
                                    GPT-4
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="actions">
                        <button @click="isShowOptions = false">Close</button>
                    </div>
                </div>
            </div>

            <div class="header" @mousedown="startDrag">
                <div class="left">
                    <h1>Speech to Text</h1>
                </div>
                <div class="right">
                    <button class="options" @click="isShowOptions = true">
                        <i class="fas fa-cog" />
                    </button>

                    <button class="close" @click="close">
                        <i class="fas fa-times" />
                    </button>
                </div>
            </div>

            <div v-if="step === 1" class="content">
                <div class="recorder">
                    <div class="controller">
                        <button
                            class="start-record"
                            @click="startRecord"
                            :disabled="
                                recorderStatus === 'RECORDING' ||
                                recorderStatus === 'STOPPED'
                            "
                        >
                            <i class="fas fa-microphone"></i>
                        </button>
                        <button
                            class="stop-record"
                            @click="stopRecord"
                            :disabled="recorderStatus !== 'RECORDING'"
                        >
                            <i class="far fa-stop-circle"></i>
                        </button>
                        <div
                            v-show="recorderStatus === 'RECORDING'"
                            class="loader loader-recording"
                        ></div>
                        <div
                            v-show="recorderStatus === 'STOPPED'"
                            class="loader loader-stopping"
                        ></div>
                        <span v-show="recorderStatus === 'READY'">
                            Press the mic button to start
                            <span v-if="isSuccessTranscribe">over</span>
                        </span>
                        <span v-show="recorderStatus === 'RECORDING'">
                            Recording...
                        </span>
                        <span v-show="recorderStatus === 'STOPPED'">
                            Processing...
                        </span>
                    </div>
                    <div class="right">
                        <!-- <button>Cancel</button> -->
                        <div class="time">{{ recordDuration.toFixed(2) }}s</div>
                    </div>
                </div>

                <div
                    class="output-recorder"
                    v-show="
                        recorderStatus === 'STOPPED' ||
                        (recorderStatus === 'READY' && isSuccessTranscribe)
                    "
                >
                    <textarea
                        :disabled="!isSuccessTranscribe"
                        :class="{ success: isSuccessTranscribe }"
                        v-model="transcriptionText"
                    ></textarea>
                </div>
            </div>

            <div v-else-if="step === 2" class="content">
                <div v-if="isLoadingResults" class="loading-prompt">
                    <div class="loader-spin"></div>
                </div>
                <div
                    v-if="!isLoadingResults && results.length === 0"
                    style="text-align: center; margin-top: 10px;"
                >
                    No results available.
                </div>
                <div class="speech-output-wrapper">
                    <div v-show="!isLoadingResults" class="guides">
                        <p class="title">Guides:</p>
                        <p>
                            There are several ways to insert the generated text
                            into the medical note field.
                        </p>
                        <ol>
                            <li>
                                Each generated text has a corresponding
                                suggested field (
                                <i class="far fa-lightbulb"></i> ). Click the
                                <span class="bold">"Apply"</span> button to
                                insert the text directly into the field.
                            </li>
                            <li>
                                To insert all the text into the suggested fields
                                at once, click the
                                <span class="bold">"Apply All"</span> button.
                            </li>
                            <li>
                                Drag and drop the text into any field you find
                                appropriate.
                            </li>
                        </ol>
                    </div>
                    <soap-speech-output
                        v-for="res in results"
                        :key="res.id"
                        :result="res"
                    />
                </div>
            </div>

            <div
                v-if="step === 1 && isSuccessTranscribe"
                class="footer next-step"
            >
                <span>
                    Edit the text if needed. If everything looks good, click
                    Next.
                </span>
                <button
                    class="btn btn-primary"
                    @click="goNext"
                    :disabled="emptySound"
                >
                    <span>Next</span>
                    <i class="fas fa-angle-right"></i>
                </button>
            </div>

            <div v-if="step === 2" class="footer step2">
                <button
                    class="btn btn-secondary"
                    :disabled="applyAllDisabled"
                    @click="close"
                >
                    Cancel
                </button>

                <div class="right">
                    <button
                        class="btn btn-secondary"
                        :disabled="applyAllDisabled"
                        @click="applyAll"
                    >
                        Apply All
                    </button>
                    <button
                        class="btn btn-primary"
                        :disabled="isLoadingResults"
                        @click="close"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SoapSpeechOutput from "./soap-speech-output.vue";

const READY = "READY";
const RECORDING = "RECORDING";
const STOPPED = "STOPPED";
const LS_KEY = "SPEECH_OPTIONS";

export default {
    components: {
        SoapSpeechOutput
    },
    data() {
        return {
            mouseOffset: { x: 0, y: 0 },
            dragging: false,
            modalDimensions: { width: 0, height: 0 },
            position: {
                top: 100,
                left: (window.innerWidth - 600) / 2 // center horizontally based on width
            },
            recorderStatus: READY,
            transcriptionText: "Loading",
            audioStream: null,
            mediaRecorder: null,
            audioBlob: null,
            isSuccessTranscribe: false,
            step: 1,
            results: [],
            isLoadingResults: true,
            isShowOptions: false,
            options: getSavedOptions(),
            recordTimer: null,
            recordDuration: 0,
            emptySound: false
        };
    },
    watch: {
        options: {
            handler(newVal) {
                localStorage.setItem(LS_KEY, JSON.stringify(newVal));
            },
            deep: true
        }
    },
    computed: {
        applyAllDisabled() {
            if (this.isLoadingResults) {
                return true;
            }
            if (!this.isLoadingResults && this.results.length === 0) {
                return true;
            }
            return false;
        }
    },
    mounted() {
        // cache the dimensions once the component is mounted
        const modal = this.$refs.modal;
        this.modalDimensions.width = modal.offsetWidth;
        this.modalDimensions.height = modal.offsetHeight;
    },
    beforeDestroy() {
        document
            .querySelectorAll('div[tabname="tabMedicalNote"] textarea')
            .forEach(elem => {
                elem.removeEventListener("dragover", this.onDragOver);
                elem.removeEventListener("drop", this.onDrop);
            });
    },
    methods: {
        goNext() {
            this.step = 2;

            let content = window.SPEECH_SOAP_CONTENT || [];
            let partitions = [];

            for (let item of content) {
                partitions = partitions.concat(extractLabels(item));
            }

            let validTextareas = [];
            let labels = [];

            for (let part of partitions) {
                let textarea = document.querySelector(
                    `textarea[id="${part.id}"]`
                );

                if (!textarea) {
                    continue;
                }

                validTextareas.push(part);
                labels.push(part.label);
            }

            this.readyDropZones();

            let sectionsStr = JSON.stringify(labels);
            let prompt = `${this.transcriptionText}\n---\nBased on the text above, insert any relevant information into the appropriate section list:${sectionsStr}.`;

            setTimeout(() => {
                if (this.options.llm.startsWith("gpt")) {
                    this.sendPromptGPT(prompt, validTextareas);
                } else {
                    this.sendPromptBedrock(prompt, validTextareas);
                }
            }, 500);
        },
        async sendPromptBedrock(prompt, validTextareas = []) {
            const payload = {
                model: this.options.llm,
                messages: [
                    {
                        role: "user",
                        content: [{ text: prompt }]
                    }
                ]
            };

            this.isLoadingResults = true;

            const res = await fetch(
                `${process.env.AI_BASE_URL}speech-to-text-ai`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": "HailHydra",
                        "vetter-token": localStorage.getItem("Vetter-Token")
                    },
                    body: JSON.stringify(payload)
                }
            );

            this.isLoadingResults = false;

            if (!res.ok) {
                alert("Failed to fetch data.");
            }

            // TODO: handle bedrock response
        },
        async sendPromptGPT(prompt, validTextareas = []) {
            const payload = {
                model: "gpt-4o",
                messages: [
                    {
                        role: "system",
                        content: `You are a helpful digital vet assistant. Output format must be in a JSON array of objects, example: [{"label":"Plan > Notes", "text":<hasil text>}]. Do not return in codeblock. If there is no suitable section, skip it. Do not include it in the JSON.`
                    },
                    {
                        role: "user",
                        content: prompt
                    }
                ]
            };

            this.isLoadingResults = true;

            const res = await fetch(
                "https://api.openai.com/v1/chat/completions",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${process.env.OPEN_AI_KEY}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            this.isLoadingResults = false;

            if (!res.ok) {
                alert("Failed to fetch data.");
            }

            const json = await res.json();
            const raw = json?.choices?.[0]?.message?.content ?? "[]";
            const results = JSON.parse(raw);

            for (let item of results) {
                let ta = validTextareas.find(t => t.label === item.label);
                if (!ta) {
                    continue;
                }
                item.id = ta.id;
            }

            this.results = results;
        },
        onDragOver(event) {
            event.preventDefault();
        },
        onDrop(event) {
            event.preventDefault();
            // let targetId = event.target.id;
            let value = event.dataTransfer.getData("text/plain");
            event.target.value = "";
            event.target.value = value;
            event.target.dispatchEvent(new Event("input")); // Notify Vue of the change
            // this.$emit("apply-text", targetId, value);
        },
        readyDropZones() {
            document
                .querySelectorAll('div[tabname="tabMedicalNote"] textarea')
                .forEach(elem => {
                    elem.addEventListener("dragover", this.onDragOver);
                    elem.addEventListener("drop", this.onDrop);
                });
        },
        applyAll() {
            this.results.forEach(res => {
                let te = document.querySelector(`textarea[id="${res.id}"]`);
                if (te) {
                    te.value = res.text;
                    te.dispatchEvent(new Event("input"));
                }
            });
        },
        applyText() {
            this.$emit(
                "apply-text",
                "fm_soap_subjective",
                "lost in hollywood!"
            );
        },
        async startRecord() {
            try {
                this.recordDuration = 0;
                this.recordTimer = setInterval(() => {
                    this.recordDuration += 0.1;
                }, 100);

                this.recorderStatus = RECORDING;
                this.isSuccessTranscribe = false;
                this.transcriptionText = "Loading";

                if (!navigator.mediaDevices || !window.MediaRecorder) {
                    this.recorderStatus = READY;
                    alert("Audio recording is not supported in this browser.");
                    return;
                }

                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true
                });

                this.audioStream = stream;
                this.mediaRecorder = new MediaRecorder(stream);

                const audioChunks = [];
                this.mediaRecorder.ondataavailable = event => {
                    if (event.data.size > 0) {
                        audioChunks.push(event.data);
                    }
                };

                this.mediaRecorder.onstop = () => {
                    this.audioBlob = new Blob(audioChunks, {
                        type: "audio/mp3"
                    });
                };

                this.mediaRecorder.start();
                this.recorderStatus = RECORDING;
            } catch (error) {
                if (error.name === "NotAllowedError") {
                    alert("Microphone access was denied by the user.");
                } else if (error.name === "NotFoundError") {
                    alert("No microphone found on this device.");
                } else {
                    alert("An error occurred while accessing audio devices.");
                }
                clearInterval(this.recordTimer);
                this.recorderStatus = READY;
            }
        },
        stopRecord() {
            if (
                this.mediaRecorder &&
                this.mediaRecorder.state === "recording"
            ) {
                clearInterval(this.recordTimer);
                this.recordTimer = null;

                this.mediaRecorder.stop(); // Stops the recording
                this.audioStream.getTracks().forEach(track => track.stop()); // Stops the microphone
                this.recorderStatus = STOPPED;

                setTimeout(() => {
                    if (this.audioBlob) {
                        this.getTranscription(this.audioBlob);
                    } else {
                        alert("Recording stopped, but no audio blob found.");
                    }
                }, 500);
            } else {
                console.warn("No recording to stop.");
            }
        },
        async getTranscription(audioBlob) {
            const PROMPT_MSG =
                "If you don't hear anything or if it's unclear, don't make up text, response with empty. Create paragraphs if necessary for easier reading.";

            const formData = new FormData();
            formData.append("file", audioBlob, "audio.mp3");
            formData.append("model", "whisper-1");
            formData.append("language", "en");
            formData.append("prompt", PROMPT_MSG);

            try {
                this.transcriptionText = "Loading";
                this.isSuccessTranscribe = false;

                const res = await fetch(
                    "https://api.openai.com/v1/audio/transcriptions",
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${process.env.OPEN_AI_KEY}`
                        },
                        body: formData
                    }
                );

                if (!res.ok) {
                    throw new Error("Failed to fetch transcription");
                }

                this.recorderStatus = READY;
                this.isSuccessTranscribe = true;
                const data = await res.json();

                // kalau takde apa-apa text, kosongkan je
                if (
                    !data.text ||
                    data.text.trim().length === 0 ||
                    data.text
                        ?.toLowerCase()
                        .includes("thank you for watching") ||
                    data.text?.startsWith(PROMPT_MSG.substring(0, 50))
                ) {
                    this.transcriptionText = "[No sound detected]";
                    this.emptySound = true;
                } else {
                    this.transcriptionText = data.text;
                    this.emptySound = false;
                }
            } catch (error) {
                alert(error.message);
                this.transcriptionText =
                    "An error occurred while transcribing.";
                this.recorderStatus = READY;
            }
        },
        startDrag(event) {
            this.dragging = true;
            this.mouseOffset.x = event.clientX - this.position.left;
            this.mouseOffset.y = event.clientY - this.position.top;

            document.addEventListener("mousemove", this.onDrag);
            document.addEventListener("mouseup", this.stopDrag);
        },
        onDrag(event) {
            if (!this.dragging) return;

            const newLeft = event.clientX - this.mouseOffset.x;
            const newTop = event.clientY - this.mouseOffset.y;

            const constrainedLeft = Math.min(
                Math.max(newLeft, 0),
                window.innerWidth - this.modalDimensions.width
            );
            const constrainedTop = Math.min(
                Math.max(newTop, 0),
                window.innerHeight - this.modalDimensions.height
            );

            this.position.left = constrainedLeft;
            this.position.top = constrainedTop;
        },
        stopDrag() {
            this.dragging = false;
            document.removeEventListener("mousemove", this.onDrag);
            document.removeEventListener("mouseup", this.stopDrag);
        },
        close() {
            this.$emit("close"); // close and clear memory
        }
    }
};

function extractLabels(obj, parentLabel = "") {
    let result = [];

    if (obj.label && obj.id) {
        const currentLabel = parentLabel
            ? `${parentLabel} > ${obj.label}`
            : obj.label;
        result.push({ label: currentLabel, id: obj.id });

        // Update parent label for deeper nested items
        parentLabel = currentLabel;
    }

    if (obj.data) {
        if (Array.isArray(obj.data)) {
            // If `data` is an array, iterate through its elements
            obj.data.forEach(item => {
                result = result.concat(extractLabels(item, parentLabel));
            });
        } else if (typeof obj.data === "object") {
            // If `data` is an object, iterate through its keys
            Object.values(obj.data).forEach(item => {
                result = result.concat(extractLabels(item, parentLabel));
            });
        }
    }

    return result;
}

function getSavedOptions() {
    let raw = localStorage.getItem(LS_KEY);

    if (!raw) {
        return {
            llm: "gpt-4o",
            speech: "openai"
        };
    }

    return JSON.parse(raw);
}
</script>

<style lang="scss" scoped>
.speech-overlay {
    #soap-speech-to-text {
        position: fixed;
        top: 100px;
        width: 600px;
        // height: 700px;
        background: white;
        border-radius: 14px;
        box-shadow: 0px 12px 26px -9px #000000;
        border: 1px solid #e2e2e2;
        display: flex;
        flex-direction: column;
        z-index: 1020;
        overflow: hidden;

        &.options {
            height: 500px;
        }

        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 20px;
            border-bottom: 1px solid #e2e2e2;
            user-select: none;

            .left {
                display: flex;
                align-items: center;
                gap: 7px;

                h1 {
                    margin: 0;
                    font-size: 1.2em;
                }
            }

            .right {
                display: flex;
                gap: 10px;
                user-select: none;
                align-items: center;

                button {
                    background: none;
                    border: none;
                    font-size: 1.4em;
                    outline: none;
                    color: gray;
                }

                button.close {
                    margin-left: 15px;
                }

                button.options {
                    font-size: 1.2em;
                    opacity: 0.7;

                    &:hover {
                        opacity: 1;
                    }
                }
            }
        }

        .content {
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow-y: scroll;
            max-height: 500px;

            .recorder {
                padding: 15px;
                display: flex;
                align-items: center;
                justify-content: space-between;

                .controller {
                    display: flex;
                    align-items: center;
                    gap: 5px;

                    button {
                        background: none;
                        border: none;
                        font-size: 2em;
                        color: #c21c1c;
                        outline: none;

                        &:hover {
                            color: #e92f2f;
                        }

                        &:disabled {
                            opacity: 0.5;
                            color: gray;
                        }
                    }
                }
            }

            .output-recorder {
                flex: 1;
                padding: 0 15px 10px 15px;

                textarea {
                    padding: 10px;
                    width: 100%;
                    min-height: 80px;
                    resize: none;
                    transition: all 0.3s ease;
                    border: 1px solid #c2c2c2;
                    background: #f9f9f9;
                    line-height: 1.6em;
                    box-shadow: inset 0px 3px 5px 0px #f0f0f0;
                    border-radius: 5px;
                    outline: none;

                    &:disabled {
                        opacity: 0.5;
                    }

                    &.success {
                        min-height: 400px;
                    }

                    &:focus {
                        background: #fff;
                    }
                }
            }

            .speech-output-wrapper {
                display: flex;
                flex-direction: column;
                transition: all 0.3s ease;

                .guides {
                    background: #f2f5f8;
                    padding: 20px;
                    margin: 20px;
                    border-radius: 8px;

                    p.title {
                        margin: 0;
                        font-weight: 500;
                    }

                    li {
                        margin-bottom: 5px;
                    }

                    i.fa-lightbulb {
                        color: #c83e31;
                    }

                    span.bold {
                        font-weight: 500;
                    }
                }
            }

            .loading-prompt {
                justify-content: center;
                align-items: center;
                display: flex;
                padding: 30px;
            }
        }

        .footer {
            &.next-step {
                padding: 0 20px 15px 20px;
                display: flex;
                justify-content: space-between;

                span {
                    font-size: 0.9em;
                    color: gray;
                }

                button {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: white;

                    span {
                        color: inherit;
                    }
                }
            }

            &.step2 {
                padding: 15px 20px;
                display: flex;
                justify-content: space-between;
                border-top: 1px solid gainsboro;

                .right {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
            }
        }

        .options-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba($color: #000, $alpha: 0.5);
            z-index: 1;
            border-radius: inherit;
            display: flex;
            align-items: center;
            justify-content: center;

            .options-content {
                width: 80%;
                height: 80%;
                background: white;
                padding: 15px;
                user-select: none;
                overflow-y: auto;

                h1 {
                    font-size: 1em;
                    padding-bottom: 3px;
                    background: #e1e1e1;
                    margin: 0;
                    padding: 3px;
                    margin-bottom: 10px;
                }

                .item {
                    margin-bottom: 20px;
                }

                .label {
                    font-weight: 600;
                    margin-bottom: 3px;

                    label {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        align-items: center;
                        cursor: pointer;

                        input {
                            margin-right: 5px;
                        }
                    }
                }
                .description {
                    font-size: 0.9em;
                    margin-bottom: 5px;
                    display: block;
                }

                select,
                textarea {
                    width: 100%;
                }

                .actions {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 30px;
                }
            }
        }

        .loader {
            margin-right: 5px;
        }

        .loader-recording {
            width: 15px;
            aspect-ratio: 0.75;
            --c: no-repeat linear-gradient(#000 0 0);
            background: var(--c) 0% 50%, var(--c) 50% 50%, var(--c) 100% 50%;
            animation: l7 1s infinite linear alternate;
        }
        @keyframes l7 {
            0% {
                background-size: 20% 50%, 20% 50%, 20% 50%;
            }
            20% {
                background-size: 20% 20%, 20% 50%, 20% 50%;
            }
            40% {
                background-size: 20% 100%, 20% 20%, 20% 50%;
            }
            60% {
                background-size: 20% 50%, 20% 100%, 20% 20%;
            }
            80% {
                background-size: 20% 50%, 20% 50%, 20% 100%;
            }
            100% {
                background-size: 20% 50%, 20% 50%, 20% 50%;
            }
        }

        .loader-stopping {
            height: 15px;
            aspect-ratio: 1.2;
            --c: no-repeat
                repeating-linear-gradient(90deg, #0e6996 0 20%, #0000 0 40%);
            background: var(--c) 50% 0, var(--c) 50% 100%;
            background-size: calc(500% / 6) 50%;
            animation: l10 1s infinite linear;
        }
        @keyframes l10 {
            33% {
                background-position: 0 0, 100% 100%;
            }
            66% {
                background-position: 0 100%, 100% 0;
            }
            100% {
                background-position: 50% 100%, 50% 0;
            }
        }

        .loader-spin {
            width: 50px;
            aspect-ratio: 1;
            display: grid;
        }
        .loader-spin::before,
        .loader-spin::after {
            content: "";
            grid-area: 1/1;
            --c: no-repeat radial-gradient(farthest-side, #b33434 92%, #0000);
            background: var(--c) 50% 0, var(--c) 50% 100%, var(--c) 100% 50%,
                var(--c) 0 50%;
            background-size: 12px 12px;
            animation: l12 1s infinite;
        }
        .loader-spin::before {
            margin: 4px;
            filter: hue-rotate(45deg);
            background-size: 8px 8px;
            animation-timing-function: linear;
        }

        @keyframes l12 {
            100% {
                transform: rotate(0.5turn);
            }
        }
    }
}
</style>
